import * as Icons from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ImageIcon from '@mui/icons-material/Image';
import SaveIcon from '@mui/icons-material/Save';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Checkbox,
    Chip,
    FormControlLabel,
    FormGroup,
    IconButton,
    Button as MuiButton,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {
    type IEmployeeTalkTemplateComponent,
    type IEmployeeTalkTemplatePage,
    type IEmployeeTalkTemplateSection,
    IPageAIAssistantConfig,
    type TEmployeeTalkComponentType,
} from '@sharedInterfaces/IEmployeeTalkTemplate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import Button from '@src/App/Components/Buttons/Button/Button';
import { useConfirmDeleteDialog } from '@src/App/Components/Dialogs/ConfirmDelete/confirmDelete';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { IconPicker } from '../../../Core/IconPicker';
import {
    COMPONENT_TYPE_LABELS,
    createNewComponent,
    getComponentByType,
} from '../TemplateComponents/ETTComponentFactory';
import ETTTemplatePreview from '../TemplatePreview/ETTTemplatePreview';
import styles from './ETTTemplateEditor.module.css';
import ExportTemplateDialog from './ExportTemplateDialog';
import ImportTemplateDialog from './ImportTemplateDialog';
import type { ITemplate } from './types';

export interface IETTTemplateEditorProps {
    initialTemplate: ITemplate;
    onSave: (template: ITemplate) => Promise<void>;
}

export const ETTTemplateEditor: React.FC<IETTTemplateEditorProps> = ({ initialTemplate, onSave }) => {
    const showErrorDialog = useErrorDialog();
    const confirmDelete = useConfirmDeleteDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;
    const oftenUsed = langStrings.oftenUsed;

    // Separate working copy from the initial template
    const [workingTemplate, setWorkingTemplate] = useState<ITemplate>(initialTemplate);
    const [isPreview, setIsPreview] = useState(false);
    const [activePageIndex, setActivePageIndex] = useState(0);

    // State für Page- und Section-Titel
    const [pageTitle, setPageTitle] = useState<string>('');
    const [sectionTitle, setSectionTitle] = useState<string>('');

    // State für die Anzeige der Komponententyp-Auswahl
    const [showComponentSelector, setShowComponentSelector] = useState<{
        pageId: string;
        sectionId: string;
        columnId: string;
    } | null>(null);

    // State für Icon-Picker
    const [iconPickerOpen, setIconPickerOpen] = useState(false);
    const [iconPickerPageId, setIconPickerPageId] = useState<string>('');

    // Update working template when initial template changes, but only on first load
    useEffect(() => {
        // Nur beim ersten Laden setzen, nicht bei jeder Änderung von initialTemplate
        if (!workingTemplate || workingTemplate.id !== initialTemplate.id) {
            setWorkingTemplate(initialTemplate);
        }
    }, [initialTemplate, workingTemplate]);

    // State für den Speichervorgang
    const [isSaving, setIsSaving] = useState(false);

    const { openDialog } = useDialog();

    // Save changes from working template to the actual template
    const handleSaveChanges = () => {
        setIsSaving(true);
        const updatedTemplate = {
            ...workingTemplate,
            // templateVersion: incrementVersion(workingTemplate.templateVersion, hasNameOrDescriptionChanged)
        };
        onSave(updatedTemplate)
            .catch((error) => {
                console.error('Error saving template:', error);
                showErrorDialog(error);
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    // Update template name or description
    const handleUpdateTemplateInfo = (updates: Partial<ITemplate>) => {
        setWorkingTemplate({
            ...workingTemplate,
            ...updates,
        });
    };

    // Page operations
    const handleAddPage = (parentPageId?: string) => {
        const newPage: IEmployeeTalkTemplatePage = {
            id: `page-${Date.now()}`,
            title: pageTitle || `${langStrings.page} ${workingTemplate.pages.length + 1}`,
            sections: [],
            order: workingTemplate.pages.length,
        };

        if (parentPageId) {
            // Add as sub-page
            setWorkingTemplate({
                ...workingTemplate,
                pages: workingTemplate.pages.map((page) => {
                    if (page.id === parentPageId) {
                        return {
                            ...page,
                            subPages: [...(page.subPages || []), newPage],
                        };
                    }
                    return page;
                }),
            });
        } else {
            // Add as main page
            setWorkingTemplate({
                ...workingTemplate,
                pages: [...workingTemplate.pages, newPage],
            });
            setActivePageIndex(workingTemplate.pages.length);
        }

        setPageTitle('');
    };

    const handleUpdatePage = (pageId: string, updates: Partial<IEmployeeTalkTemplatePage>) => {
        setWorkingTemplate({
            ...workingTemplate,
            pages: workingTemplate.pages.map((page) => {
                if (page.id === pageId) {
                    return { ...page, ...updates };
                }
                // Check sub-pages
                if (page.subPages) {
                    const updatedSubPages = page.subPages.map((subPage) =>
                        subPage.id === pageId ? { ...subPage, ...updates } : subPage,
                    );
                    if (updatedSubPages.some((subPage) => subPage.id === pageId)) {
                        return { ...page, subPages: updatedSubPages };
                    }
                }
                return page;
            }),
        });
    };

    const handleDeletePage = async (pageId: string) => {
        const page = workingTemplate.pages.find((p) => p.id === pageId);
        if (!page) return;

        const confirmed = await confirmDelete(page.title, langStrings.confirmDeletePage);
        if (confirmed) {
            setWorkingTemplate({
                ...workingTemplate,
                pages: workingTemplate.pages.filter((page) => page.id !== pageId),
            });

            // Adjust active page index if needed
            if (activePageIndex >= workingTemplate.pages.length - 1) {
                setActivePageIndex(Math.max(0, workingTemplate.pages.length - 2));
            }
        }
    };

    const handleMovePage = (pageId: string, direction: 'up' | 'down') => {
        const pageIndex = workingTemplate.pages.findIndex((p) => p.id === pageId);
        if (pageIndex === -1) return;

        if (
            (direction === 'up' && pageIndex === 0) ||
            (direction === 'down' && pageIndex === workingTemplate.pages.length - 1)
        ) {
            return;
        }

        const newIndex = direction === 'up' ? pageIndex - 1 : pageIndex + 1;
        const updatedPages = [...workingTemplate.pages];
        [updatedPages[pageIndex], updatedPages[newIndex]] = [updatedPages[newIndex], updatedPages[pageIndex]];

        // Update order property
        updatedPages.forEach((page, idx) => {
            page.order = idx;
        });

        setWorkingTemplate({
            ...workingTemplate,
            pages: updatedPages,
        });

        // Update active page index
        if (activePageIndex === pageIndex) {
            setActivePageIndex(newIndex);
        } else if (activePageIndex === newIndex) {
            setActivePageIndex(pageIndex);
        }
    };

    // Section operations
    const handleAddSection = (pageId: string) => {
        const page = workingTemplate.pages.find((p) => p.id === pageId);
        if (!page) return;

        const newSection: IEmployeeTalkTemplateSection = {
            id: `section-${Date.now()}`,
            title: sectionTitle || `${langStrings.section} ${page.sections.length + 1}`,
            columns: [
                {
                    id: `column-${Date.now()}-1`,
                    components: [],
                },
            ],
            order: page.sections.length,
        };

        handleUpdatePage(pageId, {
            sections: [...page.sections, newSection],
        });

        setSectionTitle('');
    };

    const handleUpdateSection = (pageId: string, sectionId: string, updates: Partial<IEmployeeTalkTemplateSection>) => {
        const page = workingTemplate.pages.find((p) => p.id === pageId);
        if (!page) return;

        handleUpdatePage(pageId, {
            sections: page.sections.map((section) => (section.id === sectionId ? { ...section, ...updates } : section)),
        });
    };

    const handleDeleteSection = async (pageId: string, sectionId: string) => {
        const page = workingTemplate.pages.find((p) => p.id === pageId);
        if (!page) return;

        const section = page.sections.find((s) => s.id === sectionId);
        if (!section) return;

        const confirmed = await confirmDelete(section.title, langStrings.confirmDeleteSection);
        if (confirmed) {
            handleUpdatePage(pageId, {
                sections: page.sections.filter((section) => section.id !== sectionId),
            });
        }
    };

    const handleMoveSection = (pageId: string, sectionId: string, direction: 'up' | 'down') => {
        const page = workingTemplate.pages.find((p) => p.id === pageId);
        if (!page) return;

        const sectionIndex = page.sections.findIndex((s) => s.id === sectionId);
        if (sectionIndex === -1) return;

        if (
            (direction === 'up' && sectionIndex === 0) ||
            (direction === 'down' && sectionIndex === page.sections.length - 1)
        ) {
            return;
        }

        const newIndex = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;
        const updatedSections = [...page.sections];
        [updatedSections[sectionIndex], updatedSections[newIndex]] = [
            updatedSections[newIndex],
            updatedSections[sectionIndex],
        ];

        // Update order property
        updatedSections.forEach((section, idx) => {
            section.order = idx;
        });

        handleUpdatePage(pageId, {
            sections: updatedSections,
        });
    };

    const handleToggleColumns = (pageId: string, sectionId: string) => {
        const page = workingTemplate.pages.find((p) => p.id === pageId);
        if (!page) return;

        const section = page.sections.find((s) => s.id === sectionId);
        if (!section) return;

        if (section.columns.length === 1) {
            // Add a second column
            handleUpdateSection(pageId, sectionId, {
                columns: [
                    ...section.columns,
                    {
                        id: `column-${Date.now()}-2`,
                        components: [],
                    },
                ],
            });
        } else {
            // Merge columns
            const allComponents = section.columns.flatMap((col) => col.components);
            handleUpdateSection(pageId, sectionId, {
                columns: [
                    {
                        id: section.columns[0].id,
                        components: allComponents,
                    },
                ],
            });
        }
    };

    // Component operations
    const handleShowComponentSelector = (pageId: string, sectionId: string, columnId: string) => {
        setShowComponentSelector({ pageId, sectionId, columnId });
    };

    const handleAddComponentWithType = (
        pageId: string,
        sectionId: string,
        columnId: string,
        componentType: TEmployeeTalkComponentType,
    ) => {
        const page = workingTemplate.pages.find((p) => p.id === pageId);
        if (!page) return;

        const section = page.sections.find((s) => s.id === sectionId);
        if (!section) return;

        const columnIndex = section.columns.findIndex((col) => col.id === columnId);
        if (columnIndex === -1) return;

        const column = section.columns[columnIndex];
        const newComponent = createNewComponent(componentType, column.components.length, lang);

        // Prüfe, ob es bereits eine System-Komponente dieses Typs gibt
        if (newComponent.system) {
            const hasSystemComponent = column.components.some((comp) => comp.type === componentType && comp.system);

            if (hasSystemComponent) {
                showErrorDialog(new Error(langStrings.cannotAddMultipleSystemComponents));
                return;
            }
        }

        const updatedColumns = [...section.columns];
        updatedColumns[columnIndex] = {
            ...column,
            components: [...column.components, newComponent],
        };

        handleUpdateSection(pageId, sectionId, { columns: updatedColumns });
        setShowComponentSelector(null);
    };

    const handleEditComponent = (
        pageId: string,
        sectionId: string,
        columnId: string,
        componentId: string,
        updates: Partial<IEmployeeTalkTemplateComponent>,
    ) => {
        const page = workingTemplate.pages.find((p) => p.id === pageId);
        if (!page) return;

        const section = page.sections.find((s) => s.id === sectionId);
        if (!section) return;

        const columnIndex = section.columns.findIndex((col) => col.id === columnId);
        if (columnIndex === -1) return;

        const column = section.columns[columnIndex];
        const component = column.components.find((comp) => comp.id === componentId);
        if (!component) return;

        // Stelle sicher, dass das system-Flag erhalten bleibt
        const updatedComponent = {
            ...component,
            ...updates,
            system: component.system, // Bewahre das system-Flag
        };

        const updatedComponents = column.components.map((comp) => (comp.id === componentId ? updatedComponent : comp));

        const updatedColumns = [...section.columns];
        updatedColumns[columnIndex] = {
            ...column,
            components: updatedComponents,
        };

        handleUpdateSection(pageId, sectionId, { columns: updatedColumns });
    };

    const handleDeleteComponent = async (pageId: string, sectionId: string, columnId: string, componentId: string) => {
        const page = workingTemplate.pages.find((p) => p.id === pageId);
        if (!page) return;

        const section = page.sections.find((s) => s.id === sectionId);
        if (!section) return;

        const columnIndex = section.columns.findIndex((col) => col.id === columnId);
        if (columnIndex === -1) return;

        const column = section.columns[columnIndex];
        const component = column.components.find((c) => c.id === componentId);
        if (!component) return;

        // Prüfe, ob es sich um eine System-Komponente handelt
        if (component.system) {
            showErrorDialog(new Error(langStrings.cannotDeleteSystemComponent));
            return;
        }

        const confirmed = await confirmDelete(component.label, langStrings.confirmDeleteComponent);
        if (confirmed) {
            const updatedComponents = column.components.filter((comp) => comp.id !== componentId);

            const updatedColumns = [...section.columns];
            updatedColumns[columnIndex] = {
                ...column,
                components: updatedComponents,
            };

            handleUpdateSection(pageId, sectionId, { columns: updatedColumns });
        }
    };

    const handleMoveComponent = (
        pageId: string,
        sectionId: string,
        columnId: string,
        componentId: string,
        direction: 'up' | 'down',
    ) => {
        const page = workingTemplate.pages.find((p) => p.id === pageId);
        if (!page) return;

        const section = page.sections.find((s) => s.id === sectionId);
        if (!section) return;

        const columnIndex = section.columns.findIndex((col) => col.id === columnId);
        if (columnIndex === -1) return;

        const column = section.columns[columnIndex];
        const componentIndex = column.components.findIndex((comp) => comp.id === componentId);
        if (componentIndex === -1) return;

        if (
            (direction === 'up' && componentIndex === 0) ||
            (direction === 'down' && componentIndex === column.components.length - 1)
        ) {
            return;
        }

        const updatedComponents = [...column.components];
        const newIndex = direction === 'up' ? componentIndex - 1 : componentIndex + 1;

        [updatedComponents[componentIndex], updatedComponents[newIndex]] = [
            updatedComponents[newIndex],
            updatedComponents[componentIndex],
        ];

        const updatedColumns = [...section.columns];
        updatedColumns[columnIndex] = {
            ...column,
            components: updatedComponents,
        };

        handleUpdateSection(pageId, sectionId, { columns: updatedColumns });
    };

    // Export template as JSON
    const handleExportTemplate = () => {
        try {
            // Erstelle eine Kopie des Templates ohne die ID (wird beim Import neu generiert)
            const templateToExport = {
                ...workingTemplate,
                exportDate: new Date().toISOString(),
            };

            openDialog(<ExportTemplateDialog id="exportTemplateDialog" template={templateToExport} />);
        } catch (error) {
            console.error('Error exporting template:', error);
            showErrorDialog(error as Error);
        }
    };

    // Import template from JSON
    const handleImportTemplate = () => {
        openDialog(
            <ImportTemplateDialog
                id="importTemplateDialog"
                currentTemplate={workingTemplate}
                resolve={(importedTemplate) => {
                    if (importedTemplate) {
                        setWorkingTemplate(importedTemplate);
                    }
                }}
            />,
        );
    };

    // Handle page navigation
    const handlePageChange = (index: number) => {
        if (index >= 0 && index < workingTemplate.pages.length) {
            setActivePageIndex(index);
        }
    };

    const activePage = workingTemplate.pages[activePageIndex];

    // Icon-Picker Funktionen
    const handleOpenIconPicker = (pageId: string) => {
        setIconPickerPageId(pageId);
        setIconPickerOpen(true);
    };

    const handleIconSelect = (iconName: string) => {
        handleUpdatePage(iconPickerPageId, { icon: iconName });
        setIconPickerOpen(false);
    };

    const renderIcon = (iconName?: string) => {
        if (!iconName) return <ImageIcon />;
        const IconComponent = (Icons as any)[iconName];
        return IconComponent ? <IconComponent /> : <ImageIcon />;
    };

    return (
        <div className={styles.templateEditor}>
            {/* Template name display */}
            <Typography variant="h4" gutterBottom>
                {workingTemplate.title}
            </Typography>

            <Box className={styles.editorControls}>
                <Box>
                    <Tabs
                        value={isPreview ? 'preview' : 'edit'}
                        onChange={(_, value) => setIsPreview(value === 'preview')}
                    >
                        <Tab label={oftenUsed.edit} value="edit" />
                        <Tab label={langStrings.preview} value="preview" />
                    </Tabs>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={langStrings.exportTemplate}>
                        <IconButton onClick={handleExportTemplate} color="primary">
                            <FileDownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={langStrings.importTemplate}>
                        <IconButton onClick={handleImportTemplate} color="primary">
                            <FileUploadIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        text={langStrings.saveTemplate}
                        color="primary"
                        onClick={handleSaveChanges}
                        style={{ marginLeft: 8 }}
                        icon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        disabled={isSaving}
                    />
                </Box>
            </Box>

            {isPreview ? (
                <ETTTemplatePreview
                    template={workingTemplate}
                    initialStep={activePageIndex}
                    onStepChange={handlePageChange}
                    employeeTalkId={-1}
                />
            ) : (
                <>
                    {/* Template info editing */}
                    <Box className={styles.templateInfoEditor}>
                        <TextField
                            label={langStrings.name}
                            value={workingTemplate.title}
                            onChange={(e) => handleUpdateTemplateInfo({ title: e.target.value })}
                            fullWidth
                            margin="normal"
                        />
                        <TextField
                            label={oftenUsed.description}
                            value={workingTemplate.description}
                            onChange={(e) => handleUpdateTemplateInfo({ description: e.target.value })}
                            fullWidth
                            margin="normal"
                            multiline
                            rows={2}
                        />
                    </Box>

                    {/* Page navigation */}
                    <Box className={styles.pageNavigation}>
                        <Tabs
                            value={activePageIndex}
                            onChange={(_, value) => handlePageChange(value)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {workingTemplate.pages.map((page, index) => (
                                <Tab key={page.id} label={page.title} value={index} />
                            ))}
                        </Tabs>
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                            <TextField
                                label={langStrings.pageTitle}
                                value={pageTitle}
                                onChange={(e) => setPageTitle(e.target.value)}
                                size="small"
                                sx={{ width: 200, mr: 1 }}
                            />
                            <Button text={langStrings.addPage} icon={<AddIcon />} onClick={handleAddPage} />
                        </Box>
                    </Box>

                    {activePage && (
                        <Box key={activePage.id} className={styles.pageContainer}>
                            <Box className={styles.pageHeader}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                    <IconButton
                                        onClick={() => handleOpenIconPicker(activePage.id)}
                                        title="Icon auswählen"
                                        sx={{ border: 1, borderColor: 'divider' }}
                                    >
                                        {renderIcon(activePage.icon)}
                                    </IconButton>
                                    <TextField
                                        value={activePage.title}
                                        onChange={(e) => handleUpdatePage(activePage.id, { title: e.target.value })}
                                        variant="standard"
                                        fullWidth
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <MuiButton
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleAddPage(activePage.id)}
                                        sx={{ mr: 1 }}
                                    >
                                        Sub-Page
                                    </MuiButton>
                                    <IconButton
                                        onClick={() => handleMovePage(activePage.id, 'up')}
                                        disabled={activePageIndex === 0}
                                        title={langStrings.moveUp}
                                    >
                                        <ArrowUpwardIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => handleMovePage(activePage.id, 'down')}
                                        disabled={activePageIndex === workingTemplate.pages.length - 1}
                                        title={langStrings.moveDown}
                                    >
                                        <ArrowDownwardIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDeletePage(activePage.id)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Box>

                            {/* Sub-Pages anzeigen */}
                            {activePage.subPages && activePage.subPages.length > 0 && (
                                <Box sx={{ ml: 2, mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                        <SubdirectoryArrowRightIcon fontSize="small" />
                                        Sub-Pages
                                    </Typography>
                                    {activePage.subPages.map((subPage) => (
                                        <Box
                                            key={subPage.id}
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenIconPicker(subPage.id)}
                                                sx={{ border: 1, borderColor: 'divider' }}
                                            >
                                                {renderIcon(subPage.icon)}
                                            </IconButton>
                                            <TextField
                                                size="small"
                                                value={subPage.title}
                                                onChange={(e) =>
                                                    handleUpdatePage(subPage.id, { title: e.target.value })
                                                }
                                                sx={{ flex: 1 }}
                                            />
                                            <IconButton size="small" onClick={() => handleDeletePage(subPage.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {activePage.sections.map((section) => (
                                <Box key={section.id} className={styles.sectionContainer}>
                                    <Box className={styles.sectionHeader}>
                                        <TextField
                                            value={section.title}
                                            onChange={(e) =>
                                                handleUpdateSection(activePage.id, section.id, {
                                                    title: e.target.value,
                                                })
                                            }
                                            variant="standard"
                                            fullWidth
                                        />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconButton
                                                onClick={() =>
                                                    section.columns.length > 1 &&
                                                    handleToggleColumns(activePage.id, section.id)
                                                }
                                                color={section.columns.length === 1 ? 'default' : 'primary'}
                                                title={langStrings.oneColumn}
                                            >
                                                <ViewStreamIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() =>
                                                    section.columns.length === 1 &&
                                                    handleToggleColumns(activePage.id, section.id)
                                                }
                                                color={section.columns.length > 1 ? 'primary' : 'default'}
                                                title={langStrings.twoColumns}
                                            >
                                                <ViewColumnIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleMoveSection(activePage.id, section.id, 'up')}
                                                disabled={section.order === 0}
                                                title={langStrings.moveUp}
                                            >
                                                <ArrowUpwardIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleMoveSection(activePage.id, section.id, 'down')}
                                                disabled={section.order === activePage.sections.length - 1}
                                                title={langStrings.moveDown}
                                            >
                                                <ArrowDownwardIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleDeleteSection(activePage.id, section.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Box className={styles.sectionContent}>
                                        {section.columns.map((column) => (
                                            <Box key={column.id} className={styles.column}>
                                                {column.components.map((component) => (
                                                    <Box key={component.id}>
                                                        {getComponentByType(component.type, {
                                                            component,
                                                            isEditing: true,
                                                            onEdit: (updates) =>
                                                                handleEditComponent(
                                                                    activePage.id,
                                                                    section.id,
                                                                    column.id,
                                                                    component.id,
                                                                    updates,
                                                                ),
                                                            onDelete: () =>
                                                                handleDeleteComponent(
                                                                    activePage.id,
                                                                    section.id,
                                                                    column.id,
                                                                    component.id,
                                                                ),
                                                            onMove: (direction) =>
                                                                handleMoveComponent(
                                                                    activePage.id,
                                                                    section.id,
                                                                    column.id,
                                                                    component.id,
                                                                    direction,
                                                                ),
                                                            employeeTalkId: -1,
                                                        })}
                                                    </Box>
                                                ))}

                                                {showComponentSelector &&
                                                showComponentSelector.pageId === activePage.id &&
                                                showComponentSelector.sectionId === section.id &&
                                                showComponentSelector.columnId === column.id ? (
                                                    <Box className={styles.componentTypeSelector}>
                                                        {Object.entries(COMPONENT_TYPE_LABELS).map(([type, label]) => (
                                                            <Button
                                                                key={type}
                                                                text={label as string}
                                                                size="small"
                                                                onClick={() =>
                                                                    handleAddComponentWithType(
                                                                        activePage.id,
                                                                        section.id,
                                                                        column.id,
                                                                        type as TEmployeeTalkComponentType,
                                                                    )
                                                                }
                                                                style={{ margin: 4 }}
                                                            />
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        className={styles.addComponentButton}
                                                        onClick={() =>
                                                            handleShowComponentSelector(
                                                                activePage.id,
                                                                section.id,
                                                                column.id,
                                                            )
                                                        }
                                                    >
                                                        <AddIcon /> {langStrings.addComponent}
                                                    </Box>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ))}

                            {/* KI-Assistenten-Konfiguration für diese Seite */}
                            <Accordion sx={{ mt: 3 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SmartToyIcon
                                            color={activePage.aiAssistant?.enabled ? 'primary' : 'disabled'}
                                        />
                                        <Typography>KI-Assistent für diese Seite</Typography>
                                        {activePage.aiAssistant?.enabled && (
                                            <Chip size="small" label="Aktiv" color="primary" />
                                        )}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={3}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={activePage.aiAssistant?.enabled || false}
                                                    onChange={(e) => {
                                                        const currentConfig = activePage.aiAssistant || {
                                                            enabled: false,
                                                            agentText: '',
                                                            instructions: '',
                                                            capabilities: {
                                                                chat: true,
                                                                formHelp: true,
                                                                contextualTips: true,
                                                            },
                                                            triggers: {
                                                                onPageLoad: true,
                                                                onFormError: false,
                                                                onInactivity: 0,
                                                            },
                                                        };
                                                        handleUpdatePage(activePage.id, {
                                                            aiAssistant: {
                                                                ...currentConfig,
                                                                enabled: e.target.checked,
                                                            },
                                                        });
                                                    }}
                                                />
                                            }
                                            label="KI-Assistent für diese Seite aktivieren"
                                        />

                                        {activePage.aiAssistant?.enabled && (
                                            <>
                                                <TextField
                                                    fullWidth
                                                    label="Assistenten-Text"
                                                    multiline
                                                    rows={3}
                                                    value={activePage.aiAssistant.agentText || ''}
                                                    onChange={(e) =>
                                                        handleUpdatePage(activePage.id, {
                                                            aiAssistant: {
                                                                ...activePage.aiAssistant!,
                                                                agentText: e.target.value,
                                                            },
                                                        })
                                                    }
                                                    placeholder="Hallo! Ich helfe Ihnen bei diesem Schritt..."
                                                    helperText="Dieser Text wird dem Benutzer angezeigt, wenn der Assistent erscheint."
                                                />

                                                <TextField
                                                    fullWidth
                                                    label="KI-Anweisungen"
                                                    multiline
                                                    rows={4}
                                                    value={activePage.aiAssistant.instructions || ''}
                                                    onChange={(e) =>
                                                        handleUpdatePage(activePage.id, {
                                                            aiAssistant: {
                                                                ...activePage.aiAssistant!,
                                                                instructions: e.target.value,
                                                            },
                                                        })
                                                    }
                                                    placeholder="Du hilfst Benutzern beim Ausfüllen dieses Formulars..."
                                                    helperText="Diese Anweisungen helfen der KI, passende Antworten zu geben."
                                                />

                                                <Box>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Funktionen
                                                    </Typography>
                                                    <FormGroup row>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={
                                                                        activePage.aiAssistant.capabilities?.chat ||
                                                                        false
                                                                    }
                                                                    onChange={(e) =>
                                                                        handleUpdatePage(activePage.id, {
                                                                            aiAssistant: {
                                                                                ...activePage.aiAssistant!,
                                                                                capabilities: {
                                                                                    ...activePage.aiAssistant!
                                                                                        .capabilities,
                                                                                    chat: e.target.checked,
                                                                                },
                                                                            },
                                                                        })
                                                                    }
                                                                />
                                                            }
                                                            label="Chat"
                                                        />
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={
                                                                        activePage.aiAssistant.capabilities?.formHelp ||
                                                                        false
                                                                    }
                                                                    onChange={(e) =>
                                                                        handleUpdatePage(activePage.id, {
                                                                            aiAssistant: {
                                                                                ...activePage.aiAssistant!,
                                                                                capabilities: {
                                                                                    ...activePage.aiAssistant!
                                                                                        .capabilities,
                                                                                    formHelp: e.target.checked,
                                                                                },
                                                                            },
                                                                        })
                                                                    }
                                                                />
                                                            }
                                                            label="Formular-Hilfe"
                                                        />
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={
                                                                        activePage.aiAssistant.capabilities
                                                                            ?.contextualTips || false
                                                                    }
                                                                    onChange={(e) =>
                                                                        handleUpdatePage(activePage.id, {
                                                                            aiAssistant: {
                                                                                ...activePage.aiAssistant!,
                                                                                capabilities: {
                                                                                    ...activePage.aiAssistant!
                                                                                        .capabilities,
                                                                                    contextualTips: e.target.checked,
                                                                                },
                                                                            },
                                                                        })
                                                                    }
                                                                />
                                                            }
                                                            label="Tipps"
                                                        />
                                                    </FormGroup>
                                                </Box>

                                                <Box>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Wann soll der Assistent erscheinen?
                                                    </Typography>
                                                    <FormGroup>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={
                                                                        activePage.aiAssistant.triggers?.onPageLoad ||
                                                                        false
                                                                    }
                                                                    onChange={(e) =>
                                                                        handleUpdatePage(activePage.id, {
                                                                            aiAssistant: {
                                                                                ...activePage.aiAssistant!,
                                                                                triggers: {
                                                                                    ...activePage.aiAssistant!.triggers,
                                                                                    onPageLoad: e.target.checked,
                                                                                },
                                                                            },
                                                                        })
                                                                    }
                                                                />
                                                            }
                                                            label="Beim Laden der Seite"
                                                        />
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={
                                                                        activePage.aiAssistant.triggers?.onFormError ||
                                                                        false
                                                                    }
                                                                    onChange={(e) =>
                                                                        handleUpdatePage(activePage.id, {
                                                                            aiAssistant: {
                                                                                ...activePage.aiAssistant!,
                                                                                triggers: {
                                                                                    ...activePage.aiAssistant!.triggers,
                                                                                    onFormError: e.target.checked,
                                                                                },
                                                                            },
                                                                        })
                                                                    }
                                                                />
                                                            }
                                                            label="Bei Formular-Fehlern"
                                                        />
                                                    </FormGroup>

                                                    <TextField
                                                        type="number"
                                                        label="Nach Inaktivität (Sekunden)"
                                                        value={activePage.aiAssistant.triggers?.onInactivity || 0}
                                                        onChange={(e) =>
                                                            handleUpdatePage(activePage.id, {
                                                                aiAssistant: {
                                                                    ...activePage.aiAssistant!,
                                                                    triggers: {
                                                                        ...activePage.aiAssistant!.triggers,
                                                                        onInactivity: Number(e.target.value),
                                                                    },
                                                                },
                                                            })
                                                        }
                                                        helperText="0 = deaktiviert"
                                                        sx={{ mt: 2, maxWidth: 200 }}
                                                    />
                                                </Box>
                                            </>
                                        )}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>

                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                <TextField
                                    label={langStrings.sectionTitle}
                                    value={sectionTitle}
                                    onChange={(e) => setSectionTitle(e.target.value)}
                                    size="small"
                                    sx={{ width: 200, mr: 1 }}
                                />
                                <Button
                                    text={langStrings.addSection}
                                    icon={<AddIcon />}
                                    onClick={() => handleAddSection(activePage.id)}
                                />
                            </Box>
                        </Box>
                    )}
                </>
            )}

            {/* Icon Picker Dialog */}
            <IconPicker
                open={iconPickerOpen}
                currentIcon={
                    iconPickerPageId
                        ? workingTemplate.pages.find((p) => p.id === iconPickerPageId)?.icon ||
                          workingTemplate.pages
                              .find((p) => p.subPages?.find((sp) => sp.id === iconPickerPageId))
                              ?.subPages?.find((sp) => sp.id === iconPickerPageId)?.icon
                        : undefined
                }
                onClose={() => setIconPickerOpen(false)}
                onSelect={handleIconSelect}
            />
        </div>
    );
};

export default ETTTemplateEditor;
